// Horizontal-bar / waterfall chart for stage metrics — modeled on a browser
// Network panel. One row per metric (stage execution / fan-out agent).
//
//   DURATION → Gantt waterfall: each bar is positioned by its start offset and
//              its width is the duration. Offsets are normalized PER RUN (each
//              run starts at 0) so many runs don't smear across a huge time span,
//              and parallel engineer agents line up as overlapping bars.
//   TOKENS / COST → horizontal bars from 0; width is the value.
//
// Plain inline SVG — no chart lib.

import { useMemo, useState } from "react";
import {
  MetricKind,
  Stage,
  STAGE_LABEL,
  StageMetric,
  formatCost,
  seriesColor,
  valueOf,
} from "./types";

interface Props {
  metrics: StageMetric[];
  groupBy?: "stage" | "agent";
  height?: number; // unused for layout (height is row-driven); kept for API compat
  defaultKind?: MetricKind;
}

interface Row {
  label: string;
  color: string;
  stage: Stage;
  m: StageMetric;
  start: number; // absolute epoch ms (for ordering)
  offset: number; // ms from this run's first stage start (gantt x)
  duration: number; // ms
  value: number; // selected-kind value
}

const W = 1000;
const LABEL_W = 168;
const RIGHT_PAD = 60; // room for the value label after a bar
const RIGHT_PAD_COST = 148; // list + billed prices inline
const ROW_H = 24;
const AXIS_H = 22;

const fmtNum = (v: number) =>
  v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `${Math.round(v)}`;
const fmtMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`);

const axisLabel = (kind: MetricKind) =>
  kind === "tokens"
    ? "Tokens (in + out)"
    : kind === "cost"
      ? "Cost (USD — bars = list)"
      : "Time (per-run elapsed)";
const fmtTick = (v: number, kind: MetricKind) =>
  kind === "tokens" ? fmtNum(v) : kind === "cost" ? formatCost(v) : fmtMs(v);
const fmtValue = (v: number, kind: MetricKind) =>
  kind === "tokens" ? `${v.toLocaleString()}` : kind === "cost" ? formatCost(v) : fmtMs(v);

const MetricsChart: React.FC<Props> = ({ metrics, groupBy = "stage", defaultKind = "duration" }) => {
  const [kind, setKind] = useState<MetricKind>(defaultKind);

  const rows = useMemo<Row[]>(() => {
    // Per-run t0 = earliest stage start within that run (timestamp ≈ stage end,
    // so start = end - duration). Normalizing per run keeps every run's waterfall
    // anchored at 0 regardless of when it ran.
    const startOf = (m: StageMetric) => new Date(m.timestamp).getTime() - m.durationMs;
    const runT0 = new Map<string, number>();
    for (const m of metrics) {
      const s = startOf(m);
      const cur = runT0.get(m.runId);
      if (cur === undefined || s < cur) runT0.set(m.runId, s);
    }
    return metrics
      .map((m) => {
        const stage = m.stage as Stage;
        const start = startOf(m);
        return {
          stage,
          m,
          label: groupBy === "agent" && m.agentLabel ? m.agentLabel : STAGE_LABEL[stage] ?? stage,
          color: seriesColor(stage, groupBy === "agent" ? m.agentLabel : null),
          start,
          offset: start - (runT0.get(m.runId) ?? start),
          duration: m.durationMs,
          value: valueOf(m, kind),
        };
      })
      .sort((a, b) => a.start - b.start); // chronological; groups runs, sequences stages
  }, [metrics, groupBy, kind]);

  if (rows.length === 0) {
    return (
      <div>
        <Toggle kind={kind} setKind={setKind} />
        <div className="flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-lg" style={{ height: 200 }}>
          No metrics in view yet.
        </div>
      </div>
    );
  }

  const svgH = AXIS_H + rows.length * ROW_H + 6;
  const plotL = LABEL_W;
  const rightPad = kind === "cost" ? RIGHT_PAD_COST : RIGHT_PAD;
  const plotR = W - rightPad;
  const plotW = plotR - plotL;

  // X domain: gantt spans [0, max(offset+duration)]; value bars span [0, max(value)].
  const xMax =
    kind === "duration"
      ? Math.max(...rows.map((r) => r.offset + r.duration), 1)
      : Math.max(...rows.map((r) => r.value), 1);
  const x = (v: number) => plotL + (v / xMax) * plotW;
  const ticks = 5;

  return (
    <div>
      <Toggle kind={kind} setKind={setKind} />
      <svg viewBox={`0 0 ${W} ${svgH}`} width="100%" role="img" aria-label="Stage metrics waterfall">
        {/* vertical gridlines + top axis ticks */}
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const v = (xMax / ticks) * i;
          const px = x(v);
          return (
            <g key={`t${i}`}>
              <line x1={px} y1={AXIS_H} x2={px} y2={svgH - 6} stroke="#f1f5f9" />
              <text x={px} y={14} textAnchor="middle" fontSize="10" fill="#94a3b8">
                {fmtTick(v, kind)}
              </text>
            </g>
          );
        })}
        {/* axis label (top-left gutter) */}
        <text x={8} y={14} fontSize="10" fill="#64748b">
          {axisLabel(kind)}
        </text>
        <line x1={plotL} y1={AXIS_H} x2={plotL} y2={svgH - 6} stroke="#cbd5e1" />

        {/* rows */}
        {rows.map((r, i) => {
          const y = AXIS_H + i * ROW_H;
          const barX = kind === "duration" ? x(r.offset) : plotL;
          const barEnd = kind === "duration" ? x(r.offset + r.duration) : x(r.value);
          const barW = Math.max(barEnd - barX, 2);
          return (
            <g key={`${r.m.id}-${i}`}>
              {i % 2 === 1 && <rect x={0} y={y} width={W} height={ROW_H} fill="#f8fafc" />}
              {/* row label with stage color dot */}
              <rect x={8} y={y + ROW_H / 2 - 4} width={8} height={8} rx={2} fill={r.color} />
              <text x={22} y={y + ROW_H / 2 + 4} fontSize="11" fill="#334155">
                {r.label}
                {r.m.verdict === "fail" ? " ✗" : r.m.verdict === "pass" ? " ✓" : ""}
              </text>
              {/* bar */}
              <rect x={barX} y={y + 4} width={barW} height={ROW_H - 8} rx={2} fill={r.color} fillOpacity={0.85}>
                <title>
                  {`${r.label} · ${r.m.model}`}
                  {kind === "cost"
                    ? `\nlist ${formatCost(r.m.costUsd ?? 0)}${
                        r.m.actualCostUsd != null ? `\nbilled ${formatCost(r.m.actualCostUsd)}` : ""
                      }`
                    : `\n${fmtValue(r.value, kind)}`}
                  {kind !== "duration" ? `\nduration ${fmtMs(r.duration)}` : ""}
                  {r.m.verdict ? `\nverdict: ${r.m.verdict}` : ""}
                  {`\nrun ${r.m.runId.slice(0, 8)} · ${new Date(r.m.timestamp).toLocaleTimeString()}`}
                </title>
              </rect>
              {/* value label after the bar */}
              {kind === "cost" ? (
                <CostRowValue
                  list={r.m.costUsd ?? 0}
                  billed={r.m.actualCostUsd}
                  x={Math.min(barEnd + 5, W - rightPad + 4)}
                  y={y + ROW_H / 2 + 4}
                />
              ) : (
                <text x={Math.min(barEnd + 5, W - 4)} y={y + ROW_H / 2 + 4} fontSize="10" fill="#64748b">
                  {kind === "duration" ? fmtMs(r.duration) : fmtValue(r.value, kind)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/** List (retail) struck through when billed exists; billed shown in green inline. */
const CostRowValue: React.FC<{
  list: number;
  billed: number | null;
  x: number;
  y: number;
}> = ({ list, billed, x, y }) => {
  if (billed == null) {
    return (
      <text x={x} y={y} fontSize="10" fill="#64748b">
        {formatCost(list)}
      </text>
    );
  }
  return (
    <text x={x} y={y} fontSize="10">
      <tspan fill="#94a3b8" textDecoration="line-through">
        {formatCost(list)}
      </tspan>
      <tspan fill="#64748b"> </tspan>
      <tspan fill="#15803d" fontWeight="500">
        {formatCost(billed)}
      </tspan>
    </text>
  );
};

const Toggle: React.FC<{ kind: MetricKind; setKind: (k: MetricKind) => void }> = ({ kind, setKind }) => (
  <div className="flex justify-end mb-2">
    <div className="inline-flex border border-[#262420] overflow-hidden text-xs">
      {(["duration", "tokens", "cost"] as MetricKind[]).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => setKind(k)}
          className={`px-2.5 py-1 font-medium uppercase tracking-wide ${
            kind === k ? "bg-[#C9A260] text-[#111110]" : "bg-transparent text-[#9D9585] hover:text-[#C9A260]"
          }`}
        >
          {k === "tokens" ? "Tokens" : k === "cost" ? "Cost" : "Duration"}
        </button>
      ))}
    </div>
  </div>
);

export default MetricsChart;
